/**
 * Connected Services Store
 *
 * MobX store for managing per-user connected external services
 * (Google Drive, Gmail, OneDrive, etc.).
 *
 * Handles listing, connecting, disconnecting, and status polling.
 *
 * Design ref: IMPL-drive-oauth-per-user.md, Phase 4, Section 4.1
 */
import { makeAutoObservable, runInAction } from 'mobx'
import { rawApi } from '@/services/api'

export interface ConnectedService {
  id: string
  service_name: string
  provider: string
  provider_email: string | null
  scopes: string[] | null
  connected_at: string | null
  last_used_at: string | null
  status: string
}

export interface DriveConnectionStatus {
  connected: boolean
  mode: string | null
  provider_email: string | null
  token_valid: boolean
  last_used: string | null
  status: string | null
}

export class ConnectedServicesStore {
  services: ConnectedService[] = []
  driveStatus: DriveConnectionStatus | null = null
  isLoading = false
  error: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  async fetchServices() {
    this.isLoading = true
    this.error = null

    try {
      const response = await rawApi.get('/api/v1/user/connected-services')
      runInAction(() => {
        this.services = response.data.services
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch connected services'
        this.isLoading = false
      })
    }
  }

  async fetchDriveStatus() {
    try {
      const response = await rawApi.get(
        '/api/v1/user/connected-services/google-drive/status'
      )
      runInAction(() => {
        this.driveStatus = response.data
      })
    } catch (error: any) {
      runInAction(() => {
        this.driveStatus = {
          connected: false,
          mode: null,
          provider_email: null,
          token_valid: false,
          last_used: null,
          status: null,
        }
      })
    }
  }

  async connectGoogleDrive(mode: 'read' | 'readwrite' = 'readwrite') {
    this.isLoading = true
    this.error = null

    try {
      const response = await rawApi.get(
        `/api/v1/auth/connect/google-drive?mode=${mode}`
      )
      // Redirect user to Google consent screen
      window.location.href = response.data.authorization_url
    } catch (error: any) {
      runInAction(() => {
        this.error =
          error.response?.data?.detail || 'Failed to initiate Google Drive connection'
        this.isLoading = false
      })
    }
  }

  async disconnectGoogleDrive(mode: 'read' | 'readwrite' = 'readwrite') {
    this.isLoading = true
    this.error = null

    try {
      await rawApi.delete(`/api/v1/auth/connect/google-drive?mode=${mode}`)
      runInAction(() => {
        this.isLoading = false
        // Refresh status after disconnect
        this.fetchDriveStatus()
        this.fetchServices()
      })
    } catch (error: any) {
      runInAction(() => {
        this.error =
          error.response?.data?.detail || 'Failed to disconnect Google Drive'
        this.isLoading = false
      })
    }
  }

  clearError() {
    this.error = null
  }
}

export const connectedServicesStore = new ConnectedServicesStore()
